import type { Object3D, Texture } from 'three';
import { Color, DoubleSide, Mesh, MeshStandardMaterial } from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import type { QualitySettings } from '../../../../hooks/use-three-kitchen/quality';
import { MATERIAL_ALIAS_MAP } from '../types';

/**
 * Apply shadow settings to a model
 */
export const applyShadows = (
	model: Object3D,
	qualitySettings: QualitySettings | null,
	castShadow?: boolean,
	receiveShadow?: boolean
): void => {
	const enableShadows = qualitySettings?.shadowMapEnabled ?? true;
	const cast = castShadow ?? enableShadows;
	const receive = receiveShadow ?? enableShadows;

	model.traverse((child) => {
		if ((child as any).isMesh) {
			child.castShadow = cast;
			child.receiveShadow = receive;
		}
	});
};

/**
 * Apply environment map to a model's materials
 */
export const applyEnvironmentMapToModel = (model: Object3D, environmentMap: Texture | null): void => {
	if (!environmentMap) return;

	model.traverse((child: any) => {
		if (child.isMesh && child.material) {
			if (child.userData?.isInterface) return;

			const materials = Array.isArray(child.material) ? child.material : [child.material];

			materials.forEach((mat: any) => {
				if ('envMap' in mat && 'metalness' in mat) {
					const hasTexture = mat.map != null;
					const nameLower = (mat.name || '').toLowerCase();
					const isLikelyMetalByName =
						!hasTexture &&
						(nameLower.includes('steel') ||
							nameLower.includes('metal') ||
							nameLower.includes('brushe') ||
							nameLower.includes('aluminum') ||
							nameLower.includes('chrome') ||
							nameLower.includes('stainless') ||
							nameLower.includes('inox'));

					const isMetallic = mat.metalness > 0.3 || isLikelyMetalByName;

					if (isMetallic) {
						mat.envMap = environmentMap;
						if (isLikelyMetalByName && mat.metalness < 0.5) {
							mat.metalness = 0.8;
							mat.roughness = Math.min(mat.roughness, 0.15);
						}
						// Only boost low envMapIntensity; respect configured values up to 2.5
						if ('envMapIntensity' in mat) {
							if (mat.envMapIntensity < 0.5) mat.envMapIntensity = 0.75;
						}
					} else {
						mat.envMap = environmentMap;
						if ('envMapIntensity' in mat && mat.envMapIntensity < 0.1) {
							mat.envMapIntensity = 0.3;
						}
					}
					mat.needsUpdate = true;
				}
			});
		}
	});
};

/**
 * Apply wireframe setting to a model
 */
export const applyWireframeToModel = (model: Object3D, wireframe: boolean): void => {
	model.traverse((child: any) => {
		if (child.isMesh && child.material) {
			if (child.userData?.isInterface) return;

			const materials = Array.isArray(child.material) ? child.material : [child.material];

			materials.forEach((mat: any) => {
				if ('wireframe' in mat) {
					mat.wireframe = wireframe;
					mat.needsUpdate = true;
				}
			});
		}
	});
};

/**
 * Adjust a single material
 */
export const adjustSingleMaterial = (
	material: MeshStandardMaterial,
	materialsMap?: Map<string, MeshStandardMaterial>
): MeshStandardMaterial => {
	const materialName = material.name;

	// Hide the arch above the WOOD/LOG-INSERT fireplace (matches legacy Haxe behavior)
	if (materialName === 'Image-arch') {
		const transparentMaterial = new MeshStandardMaterial({
			color: new Color(0xffffff),
			metalness: 0.9,
			roughness: 0.1,
			transparent: true,
			opacity: 0.0,
			envMapIntensity: 0.0,
			side: DoubleSide
		});
		transparentMaterial.name = 'Image-arch';
		return transparentMaterial;
	}

	// For WoodTexture, the ColladaLoader produces a MeshLambertMaterial (not
	// MeshStandardMaterial) from the DAE's <lambert> effect. Transfer the
	// diffuse texture to a new MeshStandardMaterial with PBR properties and
	// emissive self-illumination so the recessed wood cutout is visible.
	if (materialName === 'WoodTexture' || materialName === 'WoodTexture-material') {
		const diffuseMap = (material as any).map;
		const woodMat = new MeshStandardMaterial({
			map: diffuseMap,
			side: DoubleSide,
			metalness: 0.0,
			roughness: 0.85,
			envMapIntensity: 0.8,
			transparent: false,
			opacity: 1,
			color: new Color(0xffffff)
		});
		if (diffuseMap) {
			woodMat.emissiveMap = diffuseMap;
			woodMat.emissive = new Color(0xffffff);
			woodMat.emissiveIntensity = 0.35;
		}
		woodMat.name = 'WoodTexture';
		woodMat.needsUpdate = true;
		return woodMat;
	}

	const nameLower = materialName.toLowerCase();

	const alias = MATERIAL_ALIAS_MAP[materialName] || materialName;

	if (materialsMap) {
		if (materialsMap.has(alias)) {
			const original = materialsMap.get(alias)!;
			return original.clone() as MeshStandardMaterial;
		}

		if (materialsMap.has(materialName)) {
			return materialsMap.get(materialName)!.clone() as MeshStandardMaterial;
		}
	}

	const hasTexture = material.map != null;
	const isLikelyBareMetal =
		!hasTexture &&
		(nameLower.includes('steel') ||
			nameLower.includes('metal') ||
			nameLower.includes('brushe') ||
			nameLower.includes('aluminum') ||
			nameLower.includes('chrome') ||
			nameLower.includes('stainless') ||
			nameLower.includes('inox'));

	if (isLikelyBareMetal) {
		material.metalness = 0.8;
		material.roughness = 0.1;
		if ('envMapIntensity' in material) {
			(material as any).envMapIntensity = 0.75;
		}
		material.needsUpdate = true;
	}

	return material;
};

/**
 * Create a suppressed warn function for ColladaLoader
 */
export const createSuppressedWarn = (originalWarn: typeof console.warn): typeof console.warn => {
	return (...args: unknown[]) => {
		const message = args.join(' ');
		if (message.includes('THREE.ColladaLoader') || message.includes('Z-UP coordinate system')) {
			return;
		}
		originalWarn.apply(console, args);
	};
};

/**
 * Determines whether a mesh has at least one metallic material.
 * Checks both single materials and material arrays. A material is considered
 * metallic if its metalness exceeds 0.3 or its name suggests bare metal.
 * @param child - The mesh to inspect
 */
const isMeshMetallic = (child: Mesh): boolean => {
	const { material } = child;
	if (!material) return false;

	const materials = Array.isArray(material) ? material : [material];

	return materials.some((mat: any) => {
		if (!('metalness' in mat)) return false;
		if (mat.metalness > 0.3) return true;

		const hasTexture = mat.map != null;
		const nameLower = (mat.name || '').toLowerCase();

		return !hasTexture &&
			(nameLower.includes('steel') ||
				nameLower.includes('metal') ||
				nameLower.includes('brushe') ||
				nameLower.includes('aluminum') ||
				nameLower.includes('chrome') ||
				nameLower.includes('stainless') ||
				nameLower.includes('inox'));
	});
};

/**
 * Smooth vertex normals for metallic meshes to reduce banding artifacts on flat surfaces.
 * Merges duplicate vertices at the same position and recomputes smooth normals.
 * Only processes meshes with metallic materials (metalness > 0.3 or metal-like names)
 * to avoid blocking the main thread on non-metal geometry.
 * Skips meshes that have already been processed (tagged via userData.normalsSmoothed).
 * @param model - The model to process
 * @param _qualityLevel - Optional quality level (reserved for future use)
 */
export const smoothNormalsForMetalMeshes = (model: Object3D, _qualityLevel?: number): void => {
	model.traverse((child: any) => {
		if (!(child instanceof Mesh) || !child.geometry) return;

		// Skip meshes already processed (e.g., cloned from a cached model)
		if (child.userData?.normalsSmoothed) return;

		// Only process meshes with metallic materials
		if (!isMeshMetallic(child)) return;

		const { geometry } = child;
		if (!geometry.attributes.position) return;

		// Merge vertices that are at the same position (within tolerance)
		// This consolidates duplicate vertices that cause normal discontinuities
		// 0.001 tolerance merges co-located vertices on curved Collada surfaces
		const mergedGeometry = mergeVertices(geometry, 0.001);

		// Recompute smooth normals now that vertices are merged
		mergedGeometry.computeVertexNormals();

		// Replace the mesh's geometry with the merged version
		child.geometry = mergedGeometry;

		// Tag mesh so clones skip reprocessing
		child.userData = { ...child.userData, normalsSmoothed: true };
	});
};
